import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, Star, Truck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const JobRatingCard = ({ job, token }) => {
  const [rating, setRating] = useState(job.rating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(job.comment || '');
  const [submitted, setSubmitted] = useState(!!job.rating);
  const [editing, setEditing] = useState(!job.rating);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error('Please select a star rating first');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/public/portal/${token}/rate`, { do_id: job.id, rating, comment });
      setSubmitted(true);
      setEditing(false);
      toast.success('Thank you for your feedback!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg p-4" data-testid={`job-rating-${job.document_number}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Truck size={16} className="text-[#5c1a1a]" />
        <span className="font-bold">{job.document_number}</span>
        <span className="text-sm text-gray-500">{job.delivery_date}</span>
      </div>
      {job.delivery_address && <p className="text-xs text-gray-400 mt-1">{job.delivery_address}</p>}
      <div className="flex items-center gap-1 mt-3">
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            type="button"
            disabled={!editing}
            onClick={() => setRating(s)}
            onMouseEnter={() => editing && setHover(s)}
            onMouseLeave={() => setHover(0)}
            data-testid={`star-${s}-${job.document_number}`}
            className={editing ? 'cursor-pointer' : 'cursor-default'}
          >
            <Star
              size={26}
              className={(hover || rating) >= s ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
            />
          </button>
        ))}
        {submitted && !editing && (
          <button type="button" onClick={() => setEditing(true)} className="ml-3 text-xs text-[#5c1a1a] underline" data-testid={`edit-rating-${job.document_number}`}>
            Change rating
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us about your experience (optional)"
            rows={2}
            maxLength={500}
            className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#5c1a1a]"
            data-testid={`rating-comment-${job.document_number}`}
          />
          <Button onClick={submit} disabled={saving} size="sm" className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" data-testid={`submit-rating-${job.document_number}`}>
            {saving ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </div>
      )}
      {submitted && !editing && (
        <p className="text-sm text-green-700 mt-2" data-testid={`rating-thanks-${job.document_number}`}>
          ✓ Thank you for rating this delivery{comment ? ` — "${comment}"` : ''}
        </p>
      )}
    </div>
  );
};

export default function CustomerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/public/portal/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load portal'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5c1a1a]"></div></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-md w-full"><CardContent className="py-10 text-center text-gray-600" data-testid="portal-error">{error}</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="customer-portal-page">
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#5c1a1a] text-white rounded-t-xl px-6 py-6">
          <h1 className="text-2xl font-bold">NJK Logistics Pte Ltd</h1>
          <p className="text-white/80 text-sm mt-1">Customer Billing Portal — {data.customer_name}</p>
        </div>
        <Card className="rounded-t-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <h2 className="text-lg font-bold text-gray-900">Outstanding Invoices ({data.invoices.length})</h2>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600" data-testid="portal-total-outstanding">${data.total_outstanding.toFixed(2)}</p>
              </div>
            </div>

            {data.invoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="portal-all-paid">
                All invoices are fully paid. Thank you! 🎉
              </div>
            ) : (
              <div className="space-y-4">
                {data.invoices.map((inv) => (
                  <div key={inv.id} className="border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`portal-invoice-${inv.document_number}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText size={16} className="text-[#5c1a1a]" />
                        <span className="font-bold">{inv.document_number}</span>
                        {inv.days_overdue > 0 && (
                          <Badge className="bg-red-600 text-white">OVERDUE {inv.days_overdue}d</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {inv.due_date ? `Due: ${inv.due_date}` : `Date: ${inv.invoice_date}`}
                        {inv.amount_paid > 0 && ` · Paid so far: $${inv.amount_paid.toFixed(2)}`}
                      </p>
                      <p className="text-lg font-bold text-red-600 mt-1">Balance: ${inv.balance_due.toFixed(2)}</p>
                      <a
                        href={`${API}/api/invoices/${inv.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#5c1a1a] underline mt-2"
                        data-testid={`portal-pdf-${inv.document_number}`}
                      >
                        <Download size={14} /> Download Invoice PDF
                      </a>
                    </div>
                    <div className="text-center">
                      <img
                        src={`${API}/api/invoices/${inv.id}/paynow-qr`}
                        alt="PayNow QR"
                        className="w-32 h-32 border rounded bg-white p-1 mx-auto"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Scan to pay by PayNow<br />(amount pre-filled)</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data.completed_jobs && data.completed_jobs.length > 0 && (
              <div className="mt-10" data-testid="portal-ratings-section">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Rate Your Recent Deliveries</h2>
                <p className="text-sm text-gray-500 mb-4">How did we do? Your feedback helps us serve you better.</p>
                <div className="space-y-4">
                  {data.completed_jobs.map((job) => (
                    <JobRatingCard key={job.id} job={job} token={token} />
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-8 text-center">
              Questions about your bill? Call us at +65 9836 6186 or reply to your invoice email.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
